// src/components/payments/PaypalButton.js
// ✅ Direct-to-confirmation after PayPal capture (no modal)

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

export default function PaypalButton() {
  const navigate = useNavigate();

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const options = {
    'client-id': clientId,
    currency: 'EUR',
    intent: 'capture',
    'enable-funding': 'paylater,venmo,card',
    'disable-funding': 'sofort,sepa,bancontact,eps,giropay,ideal,mybank,p24',
  };

  if (!clientId) {
    return (
      <div className="p-4 bg-yellow-900/40 border border-yellow-700 rounded text-yellow-300 text-sm">
        PayPal is not configured. Set REACT_APP_PAYPAL_CLIENT_ID in client/.env and restart.
      </div>
    );
  }

  const isLiveMode = clientId && !clientId.includes('sandbox') && clientId.length > 20;

  return (
    <div className="space-y-3">
      {isLiveMode && (
        <div className="p-2 bg-green-900/40 border border-green-700 rounded text-green-300 text-xs text-center">
          🚀 Mode Production - Paiements réels activés
        </div>
      )}

      <PayPalScriptProvider options={options}>
        <PayPalButtons
          style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
          // 1) Create order on server
          createOrder={async () => {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/paypal/create-order', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            });
            if (!res.ok) throw new Error(`Create order failed: ${res.status} ${await res.text()}`);
            const data = await res.json();
            return data.id;
          }}
          // 2) Capture & go directly to confirmation page
          onApprove={async (data) => {
            try {
              // Flag “just paid” to prevent any empty-cart redirects elsewhere
              sessionStorage.setItem('justPaid', '1');

              const token = localStorage.getItem('token');
              const res = await fetch('/api/paypal/capture-order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ orderID: data.orderID }),
              });
              if (!res.ok) throw new Error(`Capture failed: ${res.status} ${await res.text()}`);

              const details = await res.json();

              // Persist order so confirmation page can recover if nav state is lost
              sessionStorage.setItem('lastOrder', JSON.stringify(details));

              // 🚀 Go straight to confirmation (replace so back button doesn't reopen checkout)
              navigate('/order-confirmation', {
                replace: true,
                state: {
                  orderId: details.orderNumber,
                  total: details.amount,
                  paymentMethod: 'PayPal',
                  success: true,
                  orderData: details,
                },
              });
            } catch (error) {
              console.error('Payment processing error:', error);
              sessionStorage.removeItem('justPaid');
              alert('Paiement échoué, veuillez réessayer. Erreur: ' + (error?.message || 'unknown'));
            }
          }}
          onError={(err) => {
            console.error('❌ PayPal error:', err);
            sessionStorage.removeItem('justPaid');
            alert('Paiement échoué, veuillez réessayer. Erreur: ' + err.message);
          }}
          onCancel={() => {
            console.log('⚠️ Payment cancelled by user');
            sessionStorage.removeItem('justPaid');
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
